import { useEffect, useState } from 'react'
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

interface Props {
  title: string
  subtitle?: string
  steps: string[]
  currentStep: number
  // Modern API: explicit handlers
  onNext?: () => void
  onBack?: () => void
  // Legacy API: step-change + final submit
  onStepChange?: (step: number) => void
  onFinalSubmit?: () => void
  open?: boolean
  onClose: () => void
  canNext?: boolean
  isLoading?: boolean
  finalLabel?: string
  maxWidth?: string
  // Validazione inline: messaggio di errore mostrato sopra il footer.
  // `error` è controllato dal wizard; in alternativa `validate` viene chiamata
  // al click su "Avanti" e, se ritorna un messaggio, blocca l'avanzamento e lo
  // mostra (così il pulsante resta abilitato e l'utente capisce cosa manca,
  // invece di trovarsi un "Avanti" disabilitato e muto).
  error?: string | null
  validate?: () => string | null | undefined
  // Chrome-only: per i wizard la cui navigazione è dentro i singoli step (form
  // react-hook-form, azioni multiple, submit asincrono) che vogliono comunque la
  // cornice condivisa (overlay + header + stepper + brand ocean). `hideFooter`
  // toglie la barra Indietro/Avanti; `hideClose` toglie la X (es. onboarding
  // obbligatorio). In questa modalità lo step fornisce i propri pulsanti nei children.
  hideFooter?: boolean
  hideClose?: boolean
  children: React.ReactNode
}

export default function WizardShell({
  title, subtitle, steps, currentStep,
  onNext, onBack, onStepChange, onFinalSubmit,
  open, onClose,
  canNext = true, isLoading = false,
  finalLabel,
  maxWidth = 'max-w-lg',
  error, validate,
  hideFooter = false, hideClose = false,
  children,
}: Props) {
  const { t } = useTranslation()
  // `attempted` passa a true al primo "Avanti" fallito; da lì mostriamo il
  // risultato VIVO di `validate()`, così il messaggio sparisce appena l'utente
  // sistema il campo, senza aspettare un altro click. Si azzera cambiando passo.
  const [attempted, setAttempted] = useState(false)
  useEffect(() => { setAttempted(false) }, [currentStep])
  if (open === false) return null
  const isLast = currentStep === steps.length - 1

  const advance = onNext ?? (() => {
    if (isLast) onFinalSubmit?.()
    else onStepChange?.(currentStep + 1)
  })
  const handleNext = () => {
    if (validate) {
      const msg = validate()
      if (msg) { setAttempted(true); return }
    }
    setAttempted(false)
    advance()
  }
  const handleBack = onBack ?? (() => onStepChange?.(currentStep - 1))
  const liveMsg = validate ? (validate() ?? null) : null
  const shownError = error ?? (attempted ? liveMsg : null)

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className={`bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full ${maxWidth} flex flex-col max-h-[92dvh] overflow-x-hidden`}>

        {/* Header */}
        <div className="flex items-start justify-between px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
            <p className="text-xs text-ocean-600 font-medium mt-0.5">
              {t('wizardShell.stepCounter', { current: currentStep + 1, total: steps.length, label: steps[currentStep] })}
            </p>
          </div>
          {!hideClose && (
            // DV-14: screen reader altrimenti leggerebbe "pulsante vuoto".
            <button
              onClick={onClose}
              aria-label={t('wizardShell.close', 'Chiudi')}
              className="p-2.5 text-gray-400 hover:text-gray-700 rounded-xl hover:bg-gray-100 active:bg-gray-200 shrink-0"
            >
              <XMarkIcon aria-hidden="true" className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="px-4 sm:px-6 pt-3 shrink-0">
          <div className="flex items-center gap-1">
            {steps.map((label, i) => (
              <div key={label} className="flex items-center gap-1 flex-1 last:flex-none">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                  i < currentStep  ? 'bg-ocean-600 text-white' :
                  i === currentStep ? 'bg-ocean-600 text-white ring-2 ring-ocean-200' :
                                      'bg-gray-100 text-gray-400'
                }`}>
                  {i < currentStep ? <CheckIcon className="w-3.5 h-3.5" /> : i + 1}
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 rounded-full ${i < currentStep ? 'bg-ocean-600' : 'bg-gray-100'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex mt-1" style={{ justifyContent: 'space-between' }}>
            {steps.map((label, i) => (
              <span key={label}
                className={`text-[10px] font-medium truncate ${i === currentStep ? 'text-ocean-600' : 'text-gray-400'} ${i !== currentStep && i !== 0 && i !== steps.length - 1 ? 'hidden sm:block' : ''}`}
                style={{ width: `${Math.floor(100 / steps.length)}%`, textAlign: i === 0 ? 'left' : i === steps.length - 1 ? 'right' : 'center' }}>
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 min-h-0">
          {children}
        </div>

        {/* Validazione inline */}
        {!hideFooter && shownError && (
          <div className="px-4 sm:px-6 pb-1 shrink-0">
            <p role="alert" className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <ExclamationCircleIcon className="w-4 h-4 shrink-0" />
              {shownError}
            </p>
          </div>
        )}

        {/* Footer */}
        {!hideFooter && (
        <div className="flex gap-3 px-4 sm:px-6 py-3 border-t border-gray-100 shrink-0"
             style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}>
          <button
            type="button"
            onClick={() => currentStep === 0 ? onClose() : handleBack()}
            className="flex items-center gap-1 px-4 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-medium transition-colors"
          >
            {currentStep === 0 ? t('wizardShell.cancel') : <><ChevronLeftIcon className="w-4 h-4" /> {t('wizardShell.back')}</>}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!canNext || isLoading}
            className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-ocean-600 hover:bg-ocean-500 active:bg-ocean-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors"
          >
            {isLoading ? (
              <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> {t('wizardShell.saving')}</>
            ) : isLast ? (
              <><CheckIcon className="w-4 h-4" /> {finalLabel ?? t('wizardShell.save')}</>
            ) : (
              <>{t('wizardShell.next')} <ChevronRightIcon className="w-4 h-4" /></>
            )}
          </button>
        </div>
        )}
      </div>
    </div>
  )
}
